import { useEffect, useMemo, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ClipboardCopy,
  Download,
  Eye,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { FormSkeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useGpnsVlans } from "@/stores/gpns-vlans";
import type { GPNSVlan } from "@/api/schemas/gpns-vlans";
import { renderSwitchAcl } from "@/lib/switch-config";

// ─── IP validation ───────────────────────────────────────────────────────────

function isValidIpv4(value: string): boolean {
  if (!value) return true;
  const parts = value.split(".");
  if (parts.length !== 4) return false;
  return parts.every((p) => {
    if (p === "" || /[^0-9]/.test(p)) return false;
    const n = Number(p);
    return n >= 0 && n <= 255;
  });
}

function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
  const allowed = /^[0-9.]$/.test(e.key);
  const control =
    e.key === "Backspace" || e.key === "Delete" || e.key === "Tab" ||
    e.key === "ArrowLeft" || e.key === "ArrowRight" || e.metaKey || e.ctrlKey;
  if (!allowed && !control) e.preventDefault();
}

function IpInput({
  value,
  placeholder,
  onChange,
  onBlur,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
}) {
  const invalid = value !== "" && !isValidIpv4(value);
  return (
    <Input
      value={value}
      placeholder={placeholder}
      maxLength={15}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      className={cn(invalid && "border-rose/60 focus-visible:ring-rose/40")}
    />
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VENDORS = [
  { value: "aruba", label: "Aruba CX" },
  { value: "hp", label: "HP/Aruba AOS" },
  { value: "ruckus", label: "Ruckus (Brocade)" },
];

const ACL_TYPES = [
  { value: "gre_mvp", label: "GRE MVP" },
  { value: "gre2", label: "GRE2" },
  { value: "hsia", label: "HSIA" },
];

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const PORT_ROWS = 52;

// ─── Types ────────────────────────────────────────────────────────────────────

interface GeneralConfig {
  vendor: string;
  marsha: string;
  switch_name: string;
  switch_ip: string;
  location: string;
  acl_type: string;
  unauth_vlan: string;
  timezone: string;
  core_switch: boolean;
  default_route: string;
  snmp_server_location: string;
}

interface PortRow {
  _id: string;
  port: string;
  description: string;
  "native/untagged vlan": string;
  "allowed/tagged vlans": string;
  "virtual_port_#": string;
  "punch_panel_#": string;
  link_location: string;
  type: "access" | "trunk";
  "dhcp trust": boolean;
  "loop guard": boolean;
  "lacp active": boolean;
  "bpdu guard": boolean;
  "root guard": boolean;
  poe: boolean;
  acl: boolean;
  "radius port": boolean;
}

interface VlanRow extends GPNSVlan {
  selected: boolean;
  dhcpSnooping: boolean;
  igmpEnable: boolean;
  interfaceVlan: boolean;
  gpnsType: string;
  gpnsVersion: string;
}

interface VlanFilterConfig {
  network_mask: string;
  version: string;
}

// ─── Steps ────────────────────────────────────────────────────────────────────

const STEPS = [
  { n: 1, label: "General" },
  { n: 2, label: "Ports" },
  { n: 3, label: "VLANs" },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0">
      {STEPS.map((s, i) => (
        <div key={s.n} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border-2 text-[12px] font-bold transition-colors",
                s.n === current
                  ? "border-violet bg-aurora-soft text-violet"
                  : s.n < current
                    ? "border-violet/50 bg-violet/10 text-violet/70"
                    : "border-line bg-surface text-fg-mute",
              )}
            >
              {s.n}
            </div>
            <span
              className={cn(
                "text-[10px] font-medium",
                s.n === current ? "text-violet" : "text-fg-mute",
              )}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={cn(
                "mb-4 h-[2px] w-16 transition-colors",
                s.n < current ? "bg-violet/40" : "bg-line",
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({
  title,
  open,
  onClose,
  children,
  footer,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-bg-0/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl border border-line bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-fg-mute hover:bg-surface-2 hover:text-fg">
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-line px-6 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label>{label}</Label>
      {children}
      {error && <span className="text-[11px] text-rose">{error}</span>}
      {!error && hint && <span className="text-[11px] text-fg-mute">{hint}</span>}
    </div>
  );
}

// ─── Port table ───────────────────────────────────────────────────────────────

const PORT_COLS: { key: keyof PortRow; label: string; width: string; type?: "checkbox" | "dropdown" | "text" }[] = [
  { key: "port",                 label: "Port",                  width: "w-16",  type: "text" },
  { key: "description",          label: "Description",           width: "w-36",  type: "text" },
  { key: "native/untagged vlan", label: "Native/Untagged VLAN",  width: "w-32",  type: "text" },
  { key: "allowed/tagged vlans", label: "Allowed/Tagged VLANs",  width: "w-32",  type: "text" },
  { key: "virtual_port_#",       label: "Virtual Port #",        width: "w-24",  type: "text" },
  { key: "punch_panel_#",        label: "Punch Panel #",         width: "w-24",  type: "text" },
  { key: "link_location",        label: "Link Location",         width: "w-28",  type: "text" },
  { key: "type",                 label: "Type",                  width: "w-20",  type: "dropdown" },
  { key: "dhcp trust",           label: "DHCP Trust",            width: "w-20",  type: "checkbox" },
  { key: "loop guard",           label: "Loop Guard",            width: "w-20",  type: "checkbox" },
  { key: "lacp active",          label: "LACP Active",           width: "w-20",  type: "checkbox" },
  { key: "bpdu guard",           label: "BPDU Guard",            width: "w-20",  type: "checkbox" },
  { key: "root guard",           label: "Root Guard",            width: "w-20",  type: "checkbox" },
  { key: "poe",                  label: "PoE",                   width: "w-14",  type: "checkbox" },
  { key: "acl",                  label: "ACL",                   width: "w-14",  type: "checkbox" },
  { key: "radius port",          label: "Radius Port",           width: "w-24",  type: "checkbox" },
];

function makeEmptyPort(): PortRow {
  return {
    _id: crypto.randomUUID(),
    port: "", description: "", "native/untagged vlan": "", "allowed/tagged vlans": "",
    "virtual_port_#": "", "punch_panel_#": "", link_location: "", type: "access",
    "dhcp trust": false, "loop guard": false, "lacp active": false, "bpdu guard": false,
    "root guard": false, poe: false, acl: false, "radius port": false,
  };
}

function PortTable({
  rows,
  onChange,
}: {
  rows: PortRow[];
  onChange: (rows: PortRow[]) => void;
}) {
  function update(id: string, key: keyof PortRow, val: string | boolean) {
    const next = rows.map((r) => r._id === id ? { ...r, [key]: val } : r);
    // auto-switch to trunk when tagged vlans filled
    if (key === "allowed/tagged vlans" && typeof val === "string") {
      const idx = next.findIndex((r) => r._id === id);
      if (idx !== -1) next[idx] = { ...next[idx], type: val.trim() ? "trunk" : "access" };
    }
    onChange(next);
  }

  return (
    <div className="overflow-auto rounded-xl border border-line" style={{ maxHeight: "520px" }}>
      <table className="text-[11px]" style={{ minWidth: "1400px", width: "100%" }}>
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-line bg-surface-2">
            {PORT_COLS.map((c) => (
              <th key={c.key} className={cn("px-2 py-2 text-left font-semibold text-fg-mute", c.width)}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id} className="border-b border-line last:border-0 hover:bg-surface-2/50">
              {PORT_COLS.map((c) => {
                const val = row[c.key];
                if (c.type === "checkbox") {
                  return (
                    <td key={c.key} className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        aria-label={c.label}
                        checked={!!val}
                        onChange={(e) => update(row._id, c.key, e.target.checked)}
                        className="accent-violet"
                      />
                    </td>
                  );
                }
                if (c.type === "dropdown") {
                  return (
                    <td key={c.key} className="px-1 py-1">
                      <select
                        aria-label={c.label}
                        value={String(val)}
                        onChange={(e) => update(row._id, c.key, e.target.value)}
                        className="w-full rounded border border-line bg-white/10 px-1 py-0.5 font-mono text-[11px] text-fg focus:border-violet/50 focus:outline-none"
                      >
                        <option value="access">access</option>
                        <option value="trunk">trunk</option>
                      </select>
                    </td>
                  );
                }
                return (
                  <td key={c.key} className="px-1 py-1">
                    <input
                      aria-label={c.label}
                      value={String(val ?? "")}
                      onChange={(e) => update(row._id, c.key, e.target.value)}
                      className="w-full rounded border border-line bg-white/10 px-1.5 py-1 font-mono text-[11px] text-fg focus:border-violet/50 focus:outline-none focus:ring-1 focus:ring-violet/30"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── VLAN table ───────────────────────────────────────────────────────────────

const VLAN_COLS: { key: keyof VlanRow; label: string; width: string; checkbox?: boolean }[] = [
  { key: "selected",      label: "✓",            checkbox: true, width: "w-8"  },
  { key: "number",        label: "#",                             width: "w-12" },
  { key: "name",          label: "Name",                          width: "w-40" },
  { key: "dhcpSnooping",  label: "DHCP Snooping", checkbox: true, width: "w-24" },
  { key: "igmpEnable",    label: "IGMP",          checkbox: true, width: "w-16" },
  { key: "interfaceVlan", label: "Interface VLAN", checkbox: true, width: "w-28" },
  { key: "networkAddress",label: "Network Addr",                  width: "w-32" },
  { key: "cidr",          label: "CIDR",                          width: "w-12" },
  { key: "gpnsType",      label: "GPNS Type",                     width: "w-28" },
  { key: "gpnsVersion",   label: "GPNS Version",                  width: "w-28" },
];

function VlanTable({
  rows,
  onChange,
}: {
  rows: VlanRow[];
  onChange: (rows: VlanRow[]) => void;
}) {
  function update(number: string, key: keyof VlanRow, val: boolean) {
    onChange(rows.map((r) => r.number === number ? { ...r, [key]: val } : r));
  }

  function addVlan() {
    const newVlan: VlanRow = {
      number: "",
      name: "Unknown VLAN",
      netmask: [],
      cidr: "",
      networkAddress: undefined,
      ip: undefined,
      routable: false,
      wiredStatus: undefined,
      dhcp: false,
      dhcpLow: undefined,
      dhcpHigh: undefined,
      dhcpRouter: undefined,
      internetAccess: false,
      proxyArp: false,
      securityLevel: 100,
      version: [],
      type: [],
      tags: [],
      selected: true,
      dhcpSnooping: false,
      igmpEnable: false,
      interfaceVlan: false,
      gpnsType: "",
      gpnsVersion: "",
    };
    onChange([...rows, newVlan]);
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-surface p-6 text-center text-sm text-fg-mute">
        No VLANs match the selected filters.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-auto rounded-xl border border-line" style={{ maxHeight: "450px" }}>
        <table className="w-full text-[12px]" style={{ minWidth: "800px" }}>
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-line bg-surface-2">
              {VLAN_COLS.map((c) => (
                <th key={c.key} className={cn("px-2 py-2 text-left font-semibold text-fg-mute", c.width)}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.number} className="border-b border-line last:border-0 hover:bg-surface-2/50">
                {VLAN_COLS.map((c) => {
                  const val = (row as unknown as Record<string, unknown>)[c.key];
                  if (c.checkbox) {
                    return (
                      <td key={c.key} className="px-2 py-1 text-center">
                        <input
                          type="checkbox"
                          aria-label={c.label}
                          checked={!!val}
                          onChange={(e) => update(row.number, c.key, e.target.checked)}
                          className="accent-violet"
                        />
                      </td>
                    );
                  }
                  return (
                    <td key={c.key} className="px-2 py-1 font-mono text-fg-dim">
                      {String(val ?? "—")}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <Button variant="ghost" size="sm" onClick={addVlan}>
          <Plus size={13} />
          Add a VLAN
        </Button>
      </div>
    </div>
  );
}

const SELECT_CLASS = "h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50";

function copyText(text: string) {
  void navigator.clipboard?.writeText(text);
  toast.success("Copied to clipboard");
}

function downloadText(text: string, filename: string) {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function SwitchPage() {
  type WizardState = { step: number; switchIpTouched: boolean; defaultRouteTouched: boolean };
  const [{ step, switchIpTouched, defaultRouteTouched }, setWizard] = useReducer(
    (s: WizardState, p: Partial<WizardState>) => ({ ...s, ...p }),
    { step: 1, switchIpTouched: false, defaultRouteTouched: false },
  );

  // Step 1 — General
  const [general, setGeneral] = useState<GeneralConfig>({
    vendor: "aruba",
    marsha: "",
    switch_name: "",
    switch_ip: "",
    location: "",
    acl_type: "gre_mvp",
    unauth_vlan: "1050",
    timezone: "America/New_York",
    core_switch: false,
    default_route: "",
    snmp_server_location: "",
  });

  // Step 2 — Ports
  const [ports, setPorts] = useState<PortRow[]>(() =>
    Array.from({ length: PORT_ROWS }, makeEmptyPort),
  );

  // Step 3 — VLANs (from GPNS API)
  const { data: allVlans, status: vlansStatus, load: loadVlans } = useGpnsVlans();
  const [vlanFilter, setVlanFilter] = useState<VlanFilterConfig>({ network_mask: "", version: "" });
  const filterOptions = useMemo(() => {
    const netmasks = [...new Set(allVlans.flatMap((v) => v.netmask))].sort();
    const versions = [
      ...new Map(
        allVlans.flatMap((v) => v.version.map((ver) => [`${ver.year}v${ver.version}`, ver]))
      ).values(),
    ].sort((a, b) => Number(b.year) - Number(a.year));
    return { netmasks, versions };
  }, [allVlans]);

  useEffect(() => {
    if (allVlans.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVlanFilter((prev) => ({
      network_mask: prev.network_mask || filterOptions.netmasks[0] || "",
      version: prev.version || (filterOptions.versions[0] ? `${filterOptions.versions[0].year}v${filterOptions.versions[0].version}` : ""),
    }));
  }, [allVlans, filterOptions]);

  // Derive VLAN rows from GPNS data + port table tagged VLANs
  const portTaggedVlans = useMemo(() => {
    const nums = new Set<string>();
    ports.forEach((p) => {
      (p["allowed/tagged vlans"] || "").split(/[\s,]+/).forEach((n) => {
        if (n && !isNaN(Number(n))) nums.add(n);
      });
    });
    return nums;
  }, [ports]);

  const derivedVlanRows = useMemo(() => {
    if (allVlans.length === 0 || !vlanFilter.network_mask) return [];
    const filtered = allVlans.filter((v) => {
      const versionKeys = v.version.map((ver) => `${ver.year}v${ver.version}`);
      return v.netmask.includes(vlanFilter.network_mask) && versionKeys.includes(vlanFilter.version);
    });
    const gpnsNumbers = new Set(filtered.map((v) => v.number));
    const extraVlans: VlanRow[] = [];
    portTaggedVlans.forEach((num) => {
      if (!gpnsNumbers.has(num)) {
        extraVlans.push({
          number: num, name: "Unknown VLAN", netmask: [], cidr: "",
          networkAddress: undefined, ip: undefined, routable: false, wiredStatus: undefined,
          dhcp: false, dhcpLow: undefined, dhcpHigh: undefined, dhcpRouter: undefined,
          internetAccess: false, proxyArp: false, securityLevel: 100,
          version: [], type: [], tags: [],
          selected: true, dhcpSnooping: false, igmpEnable: false, interfaceVlan: false,
          gpnsType: "", gpnsVersion: "",
        });
      }
    });
    return [
      ...filtered.map((v): VlanRow => ({
        ...v,
        selected: v.number === "100" || portTaggedVlans.has(v.number),
        dhcpSnooping: false,
        igmpEnable: v.number === "850",
        interfaceVlan: v.number === "100",
        gpnsType: v.type.map((t) => t.type).join(", "),
        gpnsVersion: v.version.map((ver) => `${ver.year}v${ver.version}`).join(", "),
      })),
      ...extraVlans,
    ].sort((a, b) => Number(a.number) - Number(b.number));
  }, [allVlans, vlanFilter, portTaggedVlans]);

  const [vlanRows, setVlanRows] = useState<VlanRow[]>(derivedVlanRows);
  const [prevDerivedVlanRows, setPrevDerivedVlanRows] = useState(derivedVlanRows);
  if (derivedVlanRows !== prevDerivedVlanRows) {
    setPrevDerivedVlanRows(derivedVlanRows);
    setVlanRows(derivedVlanRows);
  }

  // Modals
  type PreviewState = { previewOpen: boolean; previewContent: string };
  const [{ previewOpen, previewContent }, setPreview] = useReducer(
    (s: PreviewState, p: Partial<PreviewState>) => ({ ...s, ...p }),
    { previewOpen: false, previewContent: "" },
  );

  function openPreview(content: string) {
    setPreview({ previewContent: content, previewOpen: true });
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Switch</span>
      </div>

      {/* Title */}
      <div>
        <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Switch</h1>
        <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
          Step through the switch configuration wizard to generate a deployable config file.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-6">
          {/* Step indicator */}
          <div className="flex justify-center pt-2">
            <StepIndicator current={step} />
          </div>

          {/* ── Step 1: General Config ──────────────────────────── */}
          {step === 1 && (
            <GeneralConfigStep
              general={general}
              setGeneral={setGeneral}
              switchIpTouched={switchIpTouched}
              setSwitchIpTouched={(v) => setWizard({ switchIpTouched: v })}
              defaultRouteTouched={defaultRouteTouched}
              setDefaultRouteTouched={(v) => setWizard({ defaultRouteTouched: v })}
              onPreview={openPreview}
              onNext={() => setWizard({ step: 2 })}
            />
          )}

          {/* ── Step 2: Ports ───────────────────────────────────── */}
          {step === 2 && (
            <div className="flex flex-col gap-5">
              <div>
                <CardTitle>Port Selection</CardTitle>
                <CardSub className="mt-0.5">{PORT_ROWS} ports</CardSub>
              </div>

              <PortTable rows={ports} onChange={setPorts} />

              <div className="flex flex-wrap gap-2 border-t border-line pt-4">
                <Button variant="ghost" size="sm" onClick={() => setWizard({ step: 1 })}>
                  <ChevronLeft size={14} />
                  Previous Step
                </Button>
                <Button variant="ghost" size="sm" onClick={() => openPreview(`# Port Preview\n${ports.flatMap((p) => p.port ? [`# Port ${p.port}: ${p.description} | ${p.type} | VLAN ${p["native/untagged vlan"]}`] : []).join("\n")}`)}>
                  <Eye size={14} />
                  Port Preview
                </Button>
                <Button className="ml-auto" size="sm" onClick={() => { setWizard({ step: 3 }); if (allVlans.length === 0) void loadVlans(); }}>
                  Next Step
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 3: VLANs ──────────────────────────────────── */}
          {step === 3 && (
            <div className="flex flex-col gap-5">
              <div>
                <CardTitle>VLAN Selection</CardTitle>
                <CardSub className="mt-0.5">
                  {vlanRows.filter((v) => v.selected).length} selected · {vlanRows.length} total
                </CardSub>
              </div>

              {vlansStatus === "loading" ? (
                <FormSkeleton lines={4} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="GPNS Subnet Length">
                      <select
                        value={vlanFilter.network_mask}
                        onChange={(e) => setVlanFilter((p) => ({ ...p, network_mask: e.target.value }))}
                        className={SELECT_CLASS}
                      >
                        {filterOptions.netmasks.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Field>
                    <Field label="Version">
                      <select
                        value={vlanFilter.version}
                        onChange={(e) => setVlanFilter((p) => ({ ...p, version: e.target.value }))}
                        className={SELECT_CLASS}
                      >
                        {filterOptions.versions.map((v) => {
                          const key = `${v.year}v${v.version}`;
                          return <option key={key} value={key}>{key}</option>;
                        })}
                      </select>
                    </Field>
                  </div>

                  <VlanTable rows={vlanRows} onChange={setVlanRows} />
                </>
              )}

              <div className="flex flex-wrap gap-2 border-t border-line pt-4">
                <Button variant="ghost" size="sm" onClick={() => setWizard({ step: 2 })}>
                  <ChevronLeft size={14} />
                  Previous Step
                </Button>
                <Button variant="ghost" size="sm" onClick={() => openPreview(`# Full Preview\n# Selected VLANs: ${vlanRows.flatMap((v) => v.selected ? [v.number] : []).join(", ")}`)}>
                  <Eye size={14} />
                  Full Preview
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal: Preview */}
      <Modal
        title="Preview"
        open={previewOpen}
        onClose={() => setPreview({ previewOpen: false })}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => downloadText(previewContent, `${general.marsha || "switch"}-preview.txt`)}>
              <Download size={13} />
              Download
            </Button>
            <Button size="sm" onClick={() => copyText(previewContent)}>
              <ClipboardCopy size={13} />
              Copy
            </Button>
          </>
        }
      >
        <pre className="min-h-[200px] rounded-xl bg-bg-0 p-4 font-mono text-[12px] leading-relaxed text-fg">
          {previewContent}
        </pre>
      </Modal>
    </div>
  );
}

function GeneralConfigStep({
  general,
  setGeneral,
  switchIpTouched,
  setSwitchIpTouched,
  defaultRouteTouched,
  setDefaultRouteTouched,
  onPreview,
  onNext,
}: {
  general: GeneralConfig;
  setGeneral: (updater: (prev: GeneralConfig) => GeneralConfig) => void;
  switchIpTouched: boolean;
  setSwitchIpTouched: (v: boolean) => void;
  defaultRouteTouched: boolean;
  setDefaultRouteTouched: (v: boolean) => void;
  onPreview: (content: string) => void;
  onNext: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <CardTitle>General Switch Configuration</CardTitle>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Vendor" hint="The brand of switch you are configuring.">
          <select value={general.vendor} onChange={(e) => setGeneral((p) => ({ ...p, vendor: e.target.value }))} className={SELECT_CLASS}>
            {VENDORS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
        </Field>

        <Field label="MARSHA">
          <Input value={general.marsha} maxLength={7} onChange={(e) => setGeneral((p) => ({ ...p, marsha: e.target.value }))} />
        </Field>

        <Field label="Switch Name">
          <Input value={general.switch_name} maxLength={50} onChange={(e) => setGeneral((p) => ({ ...p, switch_name: e.target.value }))} />
        </Field>

        <Field
          label="Switch IP"
          error={switchIpTouched && !isValidIpv4(general.switch_ip) ? "Enter a valid IPv4 address (e.g. 192.168.1.10)" : undefined}
        >
          <IpInput
            value={general.switch_ip}
            placeholder="e.g. 192.168.1.10"
            onChange={(v) => setGeneral((p) => ({ ...p, switch_ip: v }))}
            onBlur={() => setSwitchIpTouched(true)}
          />
        </Field>

        <Field label="Location" hint="Where the switch is installed (e.g. MDF 2nd Floor)">
          <Input value={general.location} maxLength={50} onChange={(e) => setGeneral((p) => ({ ...p, location: e.target.value }))} />
        </Field>

        <Field label="ACL Type" hint="Select the version of ACL table.">
          <select value={general.acl_type} onChange={(e) => setGeneral((p) => ({ ...p, acl_type: e.target.value }))} className={SELECT_CLASS}>
            {ACL_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </Field>

        <Field label="Unauthorized VLAN" hint="VLAN number for the unauthorized VLAN.">
          <Input value={general.unauth_vlan} onChange={(e) => setGeneral((p) => ({ ...p, unauth_vlan: e.target.value }))} />
        </Field>

        <Field label="Timezone">
          <select value={general.timezone} onChange={(e) => setGeneral((p) => ({ ...p, timezone: e.target.value }))} className={SELECT_CLASS}>
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Field>

        <Field label="Core Switch" hint="Leave unchecked if this is an access switch." className="sm:col-span-2">
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={general.core_switch}
              onChange={(e) => setGeneral((p) => ({ ...p, core_switch: e.target.checked }))}
              className="accent-violet"
            />
            <span className="font-medium text-fg">Core Switch</span>
          </label>
        </Field>

        {general.core_switch && (
          <Field
            label="Default Route"
            className="sm:col-span-2"
            error={defaultRouteTouched && !isValidIpv4(general.default_route) ? "Enter a valid IPv4 address (e.g. 192.168.1.1)" : undefined}
          >
            <IpInput
              value={general.default_route}
              placeholder="e.g. 192.168.1.1"
              onChange={(v) => setGeneral((p) => ({ ...p, default_route: v }))}
              onBlur={() => setDefaultRouteTouched(true)}
            />
          </Field>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        <Button variant="ghost" size="sm" onClick={() => onPreview(renderSwitchAcl(general.vendor, general.acl_type, general.switch_ip))}>
          <Eye size={14} />
          ACL Preview
        </Button>
        <Button className="ml-auto" size="sm" onClick={onNext}>
          Next Step
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}
